import React, { useState, useEffect } from "react";
import axios from "axios";
import "./EmailTemplates.css";

const API_BASE = `${process.env.REACT_APP_API_URL}/templates`;

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Fetch templates from backend
  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_BASE}/list`);
      if (res.data.success) {
        // sirf email type filter karo
        const emailTemplates = res.data.data.filter(
          (t) => t.type === "email"
        );
        setTemplates(emailTemplates);
      }
    } catch (err) {
      console.error("Failed to fetch email templates:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // ✅ Add or Update template
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        type: "email",
        templateName: templateName.trim(),
        subject: subject.trim(),
        body: body.trim(),
        variables: [],
      };

      if (editingId) {
        await axios.put(`${API_BASE}/update/${editingId}`, payload);
        setEditingId(null);
      } else {
        await axios.post(`${API_BASE}/add`, payload);
      }

      setTemplateName("");
      setSubject("");
      setBody("");

      fetchTemplates();
    } catch (err) {
      console.error("Failed to save email template:", err);
    }
  };

  // ✅ Edit template
  const handleEdit = (template) => {
    setTemplateName(template.templateName || "");
    setSubject(template.subject || "");
    setBody(template.body || "");
    setEditingId(template._id);
    document.querySelector(".template-form")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  // ✅ Delete template
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await axios.delete(`${API_BASE}/delete/${id}`);
        if (editingId === id) {
          setEditingId(null);
          setTemplateName("");
          setSubject("");
          setBody("");
        }
        fetchTemplates();
      } catch (err) {
        console.error("Failed to delete email template:", err);
      }
    }
  };

  // ✅ Search filter
  const filteredTemplates = templates.filter(
    (t) =>
      (t.templateName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (t.subject || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.body || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return <div className="loading">Loading templates...</div>;
  }

  return (
    <div className="email-templates-container">
      <h2 className="page-title">Email Templates</h2>

      <div className="template-management">
        {/* ✅ Form */}
        <div className="template-form-section">
          <h3>{editingId ? "Edit Template" : "Create New Template"}</h3>
          <form onSubmit={handleSubmit} className="template-form">
            <div className="form-group">
              <label>Template Name *</label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name"
                required
              />
            </div>

            <div className="form-group">
              <label>Subject *</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject"
                required
              />
            </div>

            <div className="form-group">
              <label>Body *</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your email content here..."
                rows={8}
                required
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingId ? "Update Template" : "Save Template"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditingId(null);
                    setTemplateName("");
                    setSubject("");
                    setBody("");
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ✅ Saved templates */}
        <div className="saved-templates-section">
          <div className="templates-header">
            <h3>Saved Templates ({templates.length})</h3>
            <div className="search-box">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="search-icon">🔍</span>
            </div>
          </div>

          {filteredTemplates.length > 0 ? (
            <div className="templates-grid">
              {filteredTemplates.map((t) => (
                <div
                  key={t._id}
                  className={`template-card ${
                    editingId === t._id ? "active" : ""
                  }`}
                >
                  <div className="template-header">
                    <h4>{t.templateName}</h4>
                    <div className="template-actions">
                      <button
                        onClick={() => handleEdit(t)}
                        className="btn-icon"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(t._id)}
                        className="btn-icon"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="template-content">
                    <p>
                      <strong>Subject:</strong> {t.subject}
                    </p>
                    <div className="template-body-preview">
                      {t.body?.length > 100
                        ? `${t.body.substring(0, 100)}...`
                        : t.body}
                    </div>
                    <div className="template-meta">
                      <span>
                        Created:{" "}
                        {t.createdAt
                          ? new Date(t.createdAt).toLocaleString()
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-templates">
              {templates.length === 0
                ? "No templates saved yet. Create your first template!"
                : "No templates match your search."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailTemplates;
